import { useEffect, useRef, useState } from "react";
import { Button, useDialogFocus } from "@hybrid-iptv/ui";
import { TrailerButton } from "./TrailerModal.js";
import { useNotifications } from "./notificationsStore.js";

export interface TitleStatus {
  kind: "streamable" | "discovery" | "loading";
  detail?: string;
}

export interface TitleCastMember {
  id: number;
  name: string;
  character?: string;
  profile_path?: string | null;
  /** A whole photo URL: a Wikimedia Commons photo under Open (Section 26 step 7). */
  photoUrl?: string;
}

export interface RelatedTitle {
  id: number;
  title: string;
  poster_path?: string | null;
  /** A whole poster URL (the provider's), for Open's related rows (Section 26). */
  posterUrl?: string;
  release_date?: string | null;
  vote_average?: number;
}

export interface TitleDetailProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  overview?: string;
  posterUrl?: string;
  backdropUrl?: string;
  rating?: number;
  genres?: string[];
  cast?: TitleCastMember[];
  /** The provider's director (Section 26); TMDB's details carry no crew. */
  director?: string;
  recommendations?: RelatedTitle[];
  similar?: RelatedTitle[];
  trailerUrl?: string;
  status?: TitleStatus;
  onPlay?: () => void;
  playLabel?: string;
  /**
   * OK on a "You May Also Like" or "Similar Titles" poster (owner, 2026-10-06):
   * true when the title is in the playlist and its details are opening; false
   * and the poster says "Not in your playlist" for a moment.
   */
  onOpenRelated?: (tmdbId: number) => boolean;
  /** Open the detail with the trailer already playing (home tile shortcut). */
  autoPlayTrailer?: boolean;
}

/**
 * Shared detail overlay for movies (and TMDB titles on Home): backdrop hero,
 * poster, rating, genres, synopsis, trailer link, and a play/resume action.
 * Focus is trapped and Escape / TV Back closes it (useDialogFocus).
 */
export function TitleDetail({
  open,
  onClose,
  title,
  subtitle,
  overview,
  posterUrl,
  backdropUrl,
  rating,
  genres,
  trailerUrl,
  status,
  onPlay,
  playLabel = "▶ Play",
  autoPlayTrailer = false,
  cast,
  director,
  recommendations,
  similar,
  onOpenRelated,
}: TitleDetailProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Section 12 title tracking: alert when this title airs on any channel.
  const tracked = useNotifications((s) => s.isTracked(title));
  const addTrackedTitle = useNotifications((s) => s.addTrackedTitle);
  const removeTrackedTitle = useNotifications((s) => s.removeTrackedTitle);
  // While the fullscreen trailer is open, Back/Escape closes the trailer first
  // (layered dialogs): the detail's own Back handler is inert until the
  // trailer closes, so one remote press never collapses two layers at once.
  const [trailerOpen, setTrailerOpen] = useState(false);
  useDialogFocus({ open, paused: trailerOpen, onClose, rootRef: dialogRef });

  // Auto-play the trailer when the detail is opened from a tile's trailer
  // shortcut — once the trailer URL resolves (provider key is instant; TMDB
  // fallback arrives with the details query). Fires at most once per detail
  // open (a ref, not a state dependency), so closing the trailer doesn't
  // instantly re-open it.
  const autoPlayedRef = useRef(false);
  useEffect(() => {
    if (open) autoPlayedRef.current = false;
  }, [open]);
  useEffect(() => {
    if (open && autoPlayTrailer && trailerUrl && !autoPlayedRef.current) {
      autoPlayedRef.current = true;
      setTrailerOpen(true);
    }
  }, [open, autoPlayTrailer, trailerUrl]);

  if (!open) return null;
  const backDrop = backdropUrl ?? posterUrl;

  return (
    <div className="title-detail__overlay" onClick={onClose} role="presentation">
      <div
        ref={dialogRef}
        className="title-detail"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="title-detail__backdrop" style={backDrop ? { backgroundImage: `url(${backDrop})` } : undefined}>
          <div className="title-detail__backdrop-shade" />
        </div>
        <div className="title-detail__content">
          {posterUrl && posterUrl !== backdropUrl && (
            <img className="title-detail__poster" src={posterUrl} alt={title} loading="lazy" />
          )}
          <div className="title-detail__info">
            <h2 className="title-detail__title">{title}</h2>
            <div className="title-detail__meta">
              {typeof rating === "number" && rating > 0 && (
                <span className="title-detail__rating" title="Rating">★ {rating.toFixed(1)}</span>
              )}
              {subtitle && <span className="title-detail__subtitle">{subtitle}</span>}
              {genres && genres.length > 0 && <span className="title-detail__genres">{genres.slice(0, 3).join(" · ")}</span>}
            </div>
            {overview && <p className="title-detail__overview">{overview}</p>}
            {director && <p className="title-detail__credit">Directed by {director}</p>}
            {status && (
              <p className={`title-detail__status title-detail__status--${status.kind}`}>
                {status.kind === "streamable" && "✓ In your playlist"}
                {status.kind === "discovery" && (status.detail ?? "Discovery title — not matched to your playlist yet")}
                {status.kind === "loading" && "Checking your playlist…"}
              </p>
            )}
            <div
              className="title-detail__actions"
              // Back to the top, banner and all, whenever the buttons get focus.
              // A frame later: the remote's own scrollIntoView runs right after
              // focus and cut a smooth scroll short, leaving the banner half off
              // the top with nothing above to press Up to (onn box, 2026-10-06).
              onFocusCapture={() => requestAnimationFrame(() => dialogRef.current?.scrollTo({ top: 0, behavior: "smooth" }))}
              onKeyDown={(event) => {
                if (event.key === "ArrowUp") dialogRef.current?.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              {onPlay && (
                <Button variant="primary" onClick={onPlay} disabled={status?.kind === "discovery"}>
                  {playLabel}
                </Button>
              )}
              <TrailerButton trailerUrl={trailerUrl} open={trailerOpen} onOpenChange={setTrailerOpen} />
              <Button
                variant="ghost"
                onClick={() => (tracked ? removeTrackedTitle(title) : addTrackedTitle(title))}
                aria-pressed={tracked}
                title={tracked ? "Stop alerting when this title airs" : "Notify me when this title airs on any channel"}
              >
                {tracked ? "🔔 Tracking" : "🔔 Track"}
              </Button>
              <Button variant="ghost" onClick={onClose}>Close</Button>
            </div>
          </div>
        </div>
        <TitleDetailExtras cast={cast} recommendations={recommendations} similar={similar} onOpenRelated={onOpenRelated} />
      </div>
    </div>
  );
}

/**
 * A cast member's photo, or their initials when TMDB has none or it fails to
 * load. A photo that failed used to leave an empty grey box, and a lazy one
 * further along the row could still be blank when the remote reached it
 * (owner, 2026-10-06: "the cast card blank gap"). Twelve small photos at most,
 * so they load with the page.
 */
function CastPhoto({ name, path, url }: { name: string; path?: string | null; url?: string }) {
  const [failed, setFailed] = useState(false);
  if ((!path && !url) || failed) {
    const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word.charAt(0)).join("").toUpperCase();
    return <span className="title-detail__cast-fallback" aria-hidden>{initials}</span>;
  }
  return <img src={path ? `https://image.tmdb.org/t/p/w185${path}` : url} alt={name} loading="lazy" onError={() => setFailed(true)} />;
}

export function TitleDetailExtras({
  cast,
  recommendations,
  similar,
  onOpenRelated,
}: Pick<TitleDetailProps, "cast" | "recommendations" | "similar" | "onOpenRelated">) {
  const visibleCast = (cast ?? []).filter((person) => person.name).slice(0, 12);
  const visibleRecommendations = (recommendations ?? []).filter((item) => item.title && (item.poster_path || item.posterUrl)).slice(0, 10);
  const visibleSimilar = (similar ?? []).filter((item) => item.title && (item.poster_path || item.posterUrl)).slice(0, 10);
  if (visibleCast.length === 0 && visibleRecommendations.length === 0 && visibleSimilar.length === 0) return null;

  return (
    <div className="title-detail__extras">
      {visibleCast.length > 0 && (
        <section className="title-detail__section" aria-labelledby="title-detail-cast">
          <h3 id="title-detail-cast">Cast</h3>
          <div className="title-detail__cast-row">
            {visibleCast.map((person) => (
              <div
                className="title-detail__cast-card"
                key={person.id}
                // Stops on the remote: with nothing to focus below the buttons,
                // Down from them went nowhere and the cast and the rows under it
                // could never be scrolled to (onn box, owner, 2026-10-06).
                tabIndex={0}
                role="group"
                aria-label={`${person.name}${person.character ? ` as ${person.character}` : ""}`}
              >
                <CastPhoto name={person.name} path={person.profile_path} url={person.photoUrl} />
                <strong>{person.name}</strong>
                {person.character && <small>{person.character}</small>}
              </div>
            ))}
          </div>
        </section>
      )}
      {visibleRecommendations.length > 0 && (
        <RelatedTitleRow title="You May Also Like" items={visibleRecommendations} onOpen={onOpenRelated} />
      )}
      {visibleSimilar.length > 0 && (
        <RelatedTitleRow title="Similar Titles" items={visibleSimilar} onOpen={onOpenRelated} />
      )}
    </div>
  );
}

/** How long "Not in your playlist" stays on a poster. */
const NOT_IN_PLAYLIST_MS = 2_500;

function RelatedTitleRow({ title, items, onOpen }: { title: string; items: RelatedTitle[]; onOpen?: (tmdbId: number) => boolean }) {
  const [notInPlaylist, setNotInPlaylist] = useState<number | null>(null);
  useEffect(() => {
    if (notInPlaylist === null) return;
    const timer = setTimeout(() => setNotInPlaylist(null), NOT_IN_PLAYLIST_MS);
    return () => clearTimeout(timer);
  }, [notInPlaylist]);
  const open = (id: number) => {
    if (!onOpen) return;
    if (!onOpen(id)) setNotInPlaylist(id);
  };
  return (
    <section className="title-detail__section" aria-labelledby={`title-detail-${title.toLowerCase().replaceAll(" ", "-")}`}>
      <h3 id={`title-detail-${title.toLowerCase().replaceAll(" ", "-")}`}>{title}</h3>
      <div className="title-detail__related-row">
        {items.map((item) => (
          <div
            className="title-detail__related-card"
            key={item.id}
            tabIndex={0}
            role="group"
            data-related-tmdb={item.id}
            aria-label={`${item.title}${item.release_date ? ` ${item.release_date.slice(0, 4)}` : ""}`}
            onClick={() => open(item.id)}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              open(item.id);
            }}
          >
            {notInPlaylist === item.id && (
              <span className="title-detail__related-note" role="status">Not in your playlist</span>
            )}
            <img src={item.poster_path ? `https://image.tmdb.org/t/p/w185${item.poster_path}` : item.posterUrl} alt={item.title} loading="lazy" />
            <strong>{item.title}</strong>
            <small>
              {item.release_date?.slice(0, 4) ?? ""}
              {typeof item.vote_average === "number" && item.vote_average > 0 ? ` · ★ ${item.vote_average.toFixed(1)}` : ""}
            </small>
          </div>
        ))}
      </div>
    </section>
  );
}
